import { useState } from 'react';
import { noop } from 'lodash';
import { removeFromCollection as apiRemoveFromCollection } from '../services/removeFromCollection';
import { optimisticallyRemoveFromCollection } from '../store/optimisticallyRemoveFromCollection';
import { raiseRemoveFromListError, raiseRemovedFromList } from '../components/toasts/toasts';
import { isCollectionKimeraError } from '../utils/isCollectionKimeraError';
import { RawCollection } from '../types';
import { useOptionalQueryClient } from './useOptionalQueryClient';
import { INVALIDATE_DELAY } from './constants';

export const useRemoveFromCollection = () => {
    const [isLoading, setIsLoading] = useState(false);
    const queryClient = useOptionalQueryClient();

    const removeFromCollection = async ({ itemId, collectionName }: { itemId: string; collectionName: string }) => {
        setIsLoading(true);
        let rollback = noop;

        try {
            if (queryClient) {
                rollback = optimisticallyRemoveFromCollection(queryClient, { itemId });
            }

            await apiRemoveFromCollection(itemId);
            setIsLoading(false);
            raiseRemovedFromList(collectionName);

            setTimeout(() => {
                queryClient?.invalidateQueries({ queryKey: ['collections'] });
            }, INVALIDATE_DELAY);
        } catch (error) {
            rollback();
            if (isCollectionKimeraError(error)) {
                if (error.response?.data.errorKey === 'resource_not_found') {
                    setTimeout(async () => {
                        await queryClient?.invalidateQueries({ queryKey: ['collections'] });
                        const collection = queryClient
                            ?.getQueryData<RawCollection[]>(['collections'])
                            ?.find((c) => c.name === collectionName);

                        if (collection && !collection.items.some((i) => i.id === itemId)) {
                            raiseRemovedFromList(collectionName);
                        }
                    }, INVALIDATE_DELAY);
                } else {
                    raiseRemoveFromListError();
                }
            }
            setIsLoading(false);
        }
    };

    return { isLoading, removeFromCollection };
};
